import { lazy, Suspense } from "react"
import type { NavItem } from "@ocean/shared/MainPanelLayout"
import { RequireAuth } from "@ocean/shared/RequireAuth"
import { ScrollToTop } from "@ocean/shared/ScrollToTop"
import { Spinner } from "@ocean/ui-kit/Spinner"
import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom"

import { useOwnerAuthStore } from "@/stores/ownerAuth.store"

import {
  LOGIN_PATH,
  PANEL_BASE_PATH,
  REQUEST_EDIT_PATH,
  REQUEST_MEMBERS_PATH,
  REQUEST_NEW_PATH,
  REQUEST_VIEW_PATH,
  REQUESTS_PATH,
} from "./route.config"

const NAV_ITEMS: NavItem[] = [
  { id: "dashboard", label: "خانه", route: PANEL_BASE_PATH, icon: "home" },
  {
    id: "requests",
    label: "درخواست‌ها",
    route: REQUESTS_PATH,
    icon: "file",
    badgeColor: "brand",
  },
]

function OwnerPanelLayout() {
  const navigate = useNavigate()
  const clearSession = useOwnerAuthStore((s) => s.clearSession)
  const cooperative = useOwnerAuthStore((s) => s.cooperative)
  return (
    <MainPanelLayout
      title="پنل مالک تعاونی"
      items={NAV_ITEMS}
      sidebarInfo={{
        type: "owner",
        cooperativeName: cooperative?.cooperativeName,
        cooperativeNationalCode: cooperative?.cooperativeNationalCode,
        ceoName: cooperative?.ceoName,
      }}
      onLogout={() => {
        clearSession()
        navigate(LOGIN_PATH, { replace: true })
      }}
    />
  )
}

const CoopOwnerLoginPage = lazy(() =>
  import("@/pages/CoopOwnerLoginPage").then((m) => ({ default: m.CoopOwnerLoginPage })),
)
const DashboardPage = lazy(() => import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })))
const RequestsPage = lazy(() => import("@/pages/RequestsPage").then((m) => ({ default: m.RequestsPage })))
const FacilityMembersPage = lazy(() =>
  import("@/pages/FacilityMembersPage").then((m) => ({ default: m.FacilityMembersPage })),
)
const CoopRequestWizardPage = lazy(() =>
  import("@/pages/CoopRequestWizardPage").then((m) => ({ default: m.CoopRequestWizardPage })),
)
const NotFoundPage = lazy(() => import("@ocean/shared/NotFoundPage").then((m) => ({ default: m.NotFoundPage })))
const MainPanelLayout = lazy(() =>
  import("@ocean/shared/MainPanelLayout").then((m) => ({ default: m.MainPanelLayout })),
)

export function AppRouter() {
  const isAuthenticated = useOwnerAuthStore((s) => s.isAuthenticated)

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center">
            <Spinner size="lg" />
          </div>
        }
      >
        <Routes>
          <Route path={LOGIN_PATH} element={<CoopOwnerLoginPage />} />

          <Route element={<RequireAuth isAuthenticated={isAuthenticated} redirectTo={LOGIN_PATH} />}>
            <Route path={PANEL_BASE_PATH} element={<OwnerPanelLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path={REQUESTS_PATH} element={<RequestsPage />} />
              <Route path={REQUEST_NEW_PATH} element={<CoopRequestWizardPage mode="create" />} />
              <Route path={REQUEST_EDIT_PATH} element={<CoopRequestWizardPage mode="edit" />} />
              <Route path={REQUEST_VIEW_PATH} element={<CoopRequestWizardPage mode="view" />} />
              <Route path={REQUEST_MEMBERS_PATH} element={<FacilityMembersPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
